/**
 * Where a visit to the sign-up page came from (docs/plans/SIGNUP_ATTRIBUTION_PLAN.md).
 *
 * The marketing site (gammapos.id) puts the visit's campaign labels on the sign-up link:
 * `my.gammapos.id/daftar?utm_source=google&utm_campaign=search-kasir-offline&landing=%2Fkasir-offline`.
 * This reads them once when the app starts, keeps them for the length of the tab's session (sessionStorage, so a
 * detour through "Masuk" does not lose them), and hands them to the sign-up. They are plain labels: nothing a person
 * typed, no name, no number.
 *
 * Nothing here may get in the way of signing up. A value that is not a short plain label is dropped, storage that
 * is unavailable (a private window) means the visit is simply not attributed, and the server ignores what it does
 * not like. An ad click id (gclid, wbraid, gbraid) identifies one person's click on an ad, so it is kept only when
 * the visitor said yes on the marketing site (cookie `gp_consent=granted`, set on .gammapos.id).
 */
export type Attribution = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  ref?: string
  landing?: string
  click_id?: string
  click_id_kind?: 'gclid' | 'wbraid' | 'gbraid'
}

const KEY = 'gp_attribution'
const LABELS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'] as const
const CLICK_IDS = ['gclid', 'wbraid', 'gbraid'] as const
const LABEL = /^[\w.\-~ ]{1,100}$/
const PATH = /^\/[a-z0-9\-/]{0,100}$/
const CLICK_ID = /^[\w.\-~]{1,150}$/

/** True when the visitor agreed to measurement on the marketing site. */
export function consented(cookies: string = typeof document === 'undefined' ? '' : document.cookie): boolean {
  return /(?:^|; )gp_consent=granted(?:;|$)/.test(cookies)
}

/** The labels of a query string, and nothing else of it. */
export function fromQuery(search: string, allowClickIds: boolean): Attribution {
  const q = new URLSearchParams(search)
  const out: Attribution = {}
  for (const k of LABELS) {
    const v = q.get(k)?.trim()
    if (v && LABEL.test(v)) out[k] = v
  }
  const landing = q.get('landing')
  if (landing && PATH.test(landing)) out.landing = landing
  if (allowClickIds) {
    for (const kind of CLICK_IDS) {
      const v = q.get(kind)
      if (v && CLICK_ID.test(v)) {
        out.click_id = v
        out.click_id_kind = kind
        break
      }
    }
  }
  return out
}

function read(store: Storage): Attribution {
  try {
    const raw = store.getItem(KEY)
    return raw ? (JSON.parse(raw) as Attribution) : {}
  } catch {
    return {} // storage refused or holds something else: the visit is not attributed
  }
}

/**
 * Called once at start. A link that carries labels replaces what was kept (the newest visit is the one that
 * counts); a page opened without any leaves what was kept alone.
 */
export function captureAttribution(
  search: string,
  store: Storage = sessionStorage,
  allowClickIds: boolean = consented(),
): void {
  const arrived = fromQuery(search, allowClickIds)
  if (Object.keys(arrived).length === 0) return
  try {
    store.setItem(KEY, JSON.stringify(arrived))
  } catch {
    /* private mode or a full store */
  }
}

/** What the sign-up sends: the kept labels, or undefined for a direct visit. A click id the visitor has since
 *  withdrawn consent for is not sent. */
export function attributionForSignup(
  store: Storage = sessionStorage,
  allowClickIds: boolean = consented(),
): Attribution | undefined {
  const kept = read(store)
  if (!allowClickIds) {
    delete kept.click_id
    delete kept.click_id_kind
  }
  return Object.keys(kept).length > 0 ? kept : undefined
}

/** After a sign-up: the visit has been counted, the next one on this tab starts fresh. */
export function forgetAttribution(store: Storage = sessionStorage): void {
  try {
    store.removeItem(KEY)
  } catch {
    /* nothing to forget */
  }
}
